import { motion } from "framer-motion";
import { MapPin, Home as HomeIcon, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { SEO } from "../components/SEO";
import { CTABand } from "../components/Shared";
import { AREA_DETAILS } from "../lib/areas";

export default function Areas() {
  return (
    <>
      <SEO
        title="Areas We Serve"
        description="Wisp Home Cleaning serves Upper Stoney Creek, Heritage Green, Summit Park, Hannon, Binbrook, Ancaster, Dundas, Waterdown and Burlington with residential and small-office cleaning."
        image="/life-family.jpg"
      />
      <section className="border-b border-slate-200 bg-white px-5 pt-16 pb-14 md:px-10">
        <div className="mx-auto grid max-w-7xl items-center gap-10 lg:grid-cols-2">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Areas We Serve</span>
            <h1 className="mt-3 font-heading text-4xl font-light tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              Local cleaning across Hamilton & surrounding communities
            </h1>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-600">
              We focus on nearby neighbourhoods so we can offer dependable, on-time service and build
              lasting relationships with the families and businesses we clean for.
            </p>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="overflow-hidden rounded-3xl border border-slate-200 shadow-lg"
          >
            <img
              src="/life-family.jpg"
              alt="A happy local family enjoying their clean, bright home"
              className="h-[300px] w-full object-cover lg:h-[360px]"
            />
          </motion.div>
        </div>
      </section>

      <section className="px-5 py-20 md:px-10">
        <div className="mx-auto grid max-w-7xl gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {AREA_DETAILS.map((a, i) => (
            <motion.div
              key={a.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
              data-testid={`area-card-${i}`}
            >
              <Link
                to={`/areas/${a.slug}`}
                data-testid={`area-link-${a.slug}`}
                className="group flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary transition-colors duration-300 group-hover:bg-primary group-hover:text-white">
                  <MapPin className="h-6 w-6" />
                </div>
                <h2 className="mt-5 font-heading text-xl font-semibold text-slate-900">{a.name}</h2>
                <p className="mt-2 flex-1 leading-relaxed text-slate-600">{a.cardBlurb}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                  Cleaning in {a.name}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="mx-auto mt-16 max-w-3xl rounded-3xl border border-slate-200 bg-white p-8 text-center md:p-12">
          <HomeIcon className="mx-auto h-8 w-8 text-primary" />
          <h3 className="mt-4 font-heading text-2xl font-medium text-slate-900">Not sure if we cover your street?</h3>
          <p className="mt-3 text-slate-600">
            If you're near any of these communities, there's a good chance we can help. Reach out and
            we'll let you know.
          </p>
          <Link
            to="/contact"
            data-testid="areas-contact-link"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 font-semibold text-primary-foreground transition-all duration-300 hover:-translate-y-0.5"
          >
            Check your area <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <CTABand />
    </>
  );
}
